import Vue from 'vue';
import {parseJSON} from '../helpers/utils';

class SocketService {
  constructor() {
    this.webSocket = null;
    this.webSocketReady = false;
    this.webreconnectTimeout =  null;
    this.webreconnectInterval = 5000;
    this.webreconnectAttempts = 15;
    this.webSocketCurrentAttempts = 0;
    this.webpingInterval = 3000;

    this.latestSocketLink = '';
    this.latestSocketParam = '';

    this.SOCKET_MESSAGE_TYPE = Object.freeze({
      PING: 'ping',
      PONG: 'pong',
      CONNECTION_ERROR: 'connection_error'
    });
  }

  connect(link, param, emit = true) {
    if (link && param) {
      this.latestSocketLink = link;
      this.latestSocketParam = param;
    }
    this.webSocket = new WebSocket(`${this.latestSocketLink}/ws/${this.latestSocketParam}`);

    this.webSocket.onopen = () => this.onOpen(emit);
    this.webSocket.onmessage = (e) => this.onMessage(e);
    this.webSocket.onerror = () => this.onError();
    this.webSocket.onclose = () => this.onClose();
  }

  reconnect() {
    this.close();
    this.connect(null, null, false);
  }

  close() {
    clearTimeout(this.webreconnectTimeout);

    if (this.webSocket) {
      this.webSocket.onmessage = null;
      this.webSocket.onclose = null;
      this.webSocket.onopen = null;
      this.webSocket.onerror = null;
      this.webSocket.close();
      this.webSocket = null;
      this.webSocketReady = false;
    }
  }

  send(data) {
    if (this.webSocket) {
      if (data.type !== 'ping') this.log('Sending', data);
      this.webSocket.send(JSON.stringify(data));
    }
  }

  ping() {
    clearTimeout(this.webreconnectTimeout);

    if(!this.webSocketReady) this.reconnect();

    this.webSocketReady = false;
    this.send({type: this.SOCKET_MESSAGE_TYPE.PING});

    this.webreconnectTimeout = setTimeout(() => {
      this.ping();
    }, this.webpingInterval)
  }

  connectErrorTimeout() {
    clearTimeout(this.webreconnectTimeout);

    this.webreconnectTimeout = setTimeout(() => {
      this.webSocketCurrentAttempts++;

      if (this.webSocketCurrentAttempts >= this.webreconnectAttempts) {
        this.close();
        clearTimeout(this.webreconnectTimeout);
      } else {
        this.reconnect();
      }
    }, this.webreconnectInterval)
  }

  onOpen(emit = true) {
    this.log('Connect');
    this.webSocketReady = true;

    if (emit)
      Vue.prototype.$eventEmitter.emit(Vue.prototype.$eventEmitter.eventTypes.SOCKETS_OPEN, this.webSocket);
    else
      Vue.prototype.$eventEmitter.emit(Vue.prototype.$eventEmitter.eventTypes.SOCKETS_RECONNECT, this.webSocket);

    this.ping();
  }

  onMessage(e) {
    const data = JSON.parse(e.data);
    if (data.type !== 'pong') this.log('Message', e);

    if (data && data.type) {
      if (data.type === this.SOCKET_MESSAGE_TYPE.PONG) {
        this.webSocketReady = true;
      } else if (data.type === this.SOCKET_MESSAGE_TYPE.CONNECTION_ERROR) {
        const errorData = JSON.parse(data.payload.message);
        if (errorData && errorData.code && errorData.code === Vue.prototype.$ERROR_CODE.NOT_ASSOCIATED_DEVICE) {
          Vue.prototype.$eventEmitter.emit(Vue.prototype.$eventEmitter.eventTypes.CLEAR_DEVICE);
        } else {
          if (Vue.prototype.$store.user) {
            Vue.prototype.$authService.refresh().then(({data}) => {
              Vue.prototype.$authService.login(data.access_token, data.refresh_token, data.user);
              this.reconnect()
            })
          }
        }
      }
    }

    Vue.prototype.$eventEmitter.emit(Vue.prototype.$eventEmitter.eventTypes.SOCKETS_MESSAGE, data);
  }

  onClose() {
    this.log('Disconnect');
    this.connectErrorTimeout();
  }

  onError() {
    this.log('Connection error');
    this.connectErrorTimeout();
  }

  log(message, ...params) {
    const date = new Date();
    const dateFinal = `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}:${date.getMilliseconds()}`;

    console.log(`[${dateFinal}][SOCKETS]: ${message}`, ...params);
  }
}

export default SocketService;
